import { Link } from "react-router-dom";
import {
  Shield,
  FileText,
  Activity,
  ArrowRight,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { useTrustConfig, useStatus } from "@/api/client";
import OverallStatus from "@/components/OverallStatus";
import ComplianceBadge from "@/components/ComplianceBadge";
import StatusBadge from "@/components/StatusBadge";
import IncidentTimeline from "@/components/IncidentTimeline";

function StatusSummary({
  components,
}: {
  components: Array<{ status: string }>;
}) {
  const operational = components.filter(
    (c) => c.status === "operational"
  ).length;
  const total = components.length;
  const issues = total - operational;

  return (
    <div className="card p-5">
      <div className="flex items-center gap-2">
        <Activity className="h-5 w-5 text-brand-primary" />
        <h2 className="text-sm font-semibold text-gray-900">
          System Status Summary
        </h2>
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="text-3xl font-bold text-gray-900">{operational}</span>
        <span className="text-sm text-gray-500">/ {total} systems operational</span>
      </div>
      {issues > 0 && (
        <p className="mt-1 text-sm text-amber-600">
          {issues} {issues === 1 ? "system" : "systems"} experiencing issues
        </p>
      )}
      <Link
        to="/status"
        className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-brand-primary hover:underline"
      >
        View full status
        <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}

function DocumentSummary() {
  return (
    <div className="card p-5">
      <div className="flex items-center gap-2">
        <FileText className="h-5 w-5 text-brand-primary" />
        <h2 className="text-sm font-semibold text-gray-900">
          Security Documents
        </h2>
      </div>
      <p className="mt-2 text-sm text-gray-500">
        Review our security policies, audit reports, and compliance
        certifications.
      </p>
      <Link
        to="/documents"
        className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-brand-primary hover:underline"
      >
        Browse documents
        <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}

function SkeletonBlock({ className }: { className?: string }) {
  return <div className={`skeleton ${className ?? "h-24 w-full"}`} />;
}

export default function HomePage() {
  const {
    data: trust,
    isLoading: trustLoading,
    error: trustError,
  } = useTrustConfig();
  const {
    data: status,
    isLoading: statusLoading,
    error: statusError,
    refetch: refetchStatus,
  } = useStatus();

  const isLoading = trustLoading || statusLoading;
  const showCompliance = trust?.tenant.show_compliance ?? false;
  const showDocuments = trust?.tenant.show_documents ?? false;
  const showStatus = trust?.tenant.show_status_page ?? true;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <SkeletonBlock className="h-16 w-full rounded-lg" />
        <div className="grid gap-4 sm:grid-cols-2">
          <SkeletonBlock className="h-32 rounded-lg" />
          <SkeletonBlock className="h-32 rounded-lg" />
        </div>
        <SkeletonBlock className="h-48 rounded-lg" />
      </div>
    );
  }

  if (trustError || statusError) {
    return (
      <div className="flex flex-col items-center py-12">
        <p className="text-sm text-gray-500">Failed to load trust center data.</p>
        <button
          onClick={() => refetchStatus()}
          className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-brand-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Retry
        </button>
      </div>
    );
  }

  const description =
    trust?.tenant.page_description ??
    `Welcome to the ${trust?.tenant.company_name ?? ""} Trust Center. View our security posture, compliance status, and system health.`;

  return (
    <div className="space-y-8">
      {/* Page description */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {trust?.tenant.page_title ?? "Trust Center"}
        </h1>
        <p className="mt-2 text-gray-500">{description}</p>
      </div>

      {/* Overall status banner */}
      {showStatus && status && (
        <OverallStatus status={status.overall_status} />
      )}

      {/* Compliance badges */}
      {showCompliance && trust?.compliance && trust.compliance.length > 0 && (
        <section>
          <div className="mb-4 flex items-center gap-2">
            <Shield className="h-5 w-5 text-brand-primary" />
            <h2 className="text-lg font-semibold text-gray-900">
              Compliance & Certifications
            </h2>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {trust.compliance.map((c) => (
              <ComplianceBadge key={c.framework_code} compliance={c} />
            ))}
          </div>
        </section>
      )}

      {/* Quick summaries */}
      <div className="grid gap-4 sm:grid-cols-2">
        {showStatus && status && (
          <StatusSummary components={status.components} />
        )}
        {showDocuments && <DocumentSummary />}
      </div>

      {/* Recent active incidents */}
      {showStatus && status && status.active_incidents.length > 0 && (
        <section>
          <IncidentTimeline
            incidents={status.active_incidents.slice(0, 3)}
            title="Active Incidents"
          />
          {status.active_incidents.length > 3 && (
            <div className="mt-4 text-center">
              <Link
                to="/status"
                className="inline-flex items-center gap-1 text-sm font-medium text-brand-primary hover:underline"
              >
                View all incidents
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          )}
        </section>
      )}

      {/* No active incidents */}
      {showStatus &&
        status &&
        status.active_incidents.length === 0 &&
        status.overall_status === "operational" && (
          <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 px-4 py-3 text-center text-sm text-emerald-700">
            No active incidents. All systems are running normally.
          </div>
        )}
    </div>
  );
}
